import React, { useState } from 'react';

import { useRutilusQuery } from '../../../api/graphql/useRutilusQuery';
import { Heading } from '../../../components/Heading/Heading';
import { ProductTable, IProductTableRow } from '../../../components/ProductTable/ProductTable';
import { Text } from '../../../components/Text/Text';
import { MILLION_USERS } from '../../../constants/constants';
import { getNodesFromConnection } from '../../../helpers/graphql';
import { useUserInfo } from '../../../helpers/hooks/useUserInfo';
import { GetSpeciesTopBaits, GetSpeciesTopBaitsVariables } from '../../../interfaces/graphql';
import { getSpeciesTopBaits } from '../../../queries/rutilus/Species';

interface IProps {
  speciesId: string;
  speciesName: string;
}

const PRO_PAGE_SIZE = 5;

function extractProducts(data?: GetSpeciesTopBaits): IProductTableRow[] {
  if (!data) {
    return [];
  }
  const nodes = getNodesFromConnection(data.topProductUnits);
  if (!nodes.length) {
    return [];
  }
  return nodes.map(({ productUnit: { product, image }, numberOfCatches, position }) => ({
    productId: product._id,
    displayName: product.displayName || '',
    imageUrl: image ? image.url : '',
    brandName: product.brand.name,
    categoryName: product.category.title || '',
    numberOfCatches,
    position,
  }));
}

export const TopBaitsSection = ({ speciesId, speciesName }: IProps) => {
  const [pageSize, setPageSize] = useState(PRO_PAGE_SIZE);
  const { isPremium } = useUserInfo();
  const { data, previousData, loading } = useRutilusQuery<
    GetSpeciesTopBaits,
    GetSpeciesTopBaitsVariables
  >(getSpeciesTopBaits, {
    variables: {
      speciesId,
      first: isPremium ? pageSize : 3,
    },
  });

  const products = extractProducts(data || previousData);

  return (
    <div data-testid="TopBaitsSection">
      <Heading className="mx-auto text-left" variant="sm">
        Top Baits
      </Heading>
      <Text className="my-5">
        {`According to catch data from over ${MILLION_USERS} million anglers on Fishbrain, these are the top baits used to catch ${speciesName}.`}
      </Text>
      <div className="md:w-col-6 -mx-6 sm:-mx-11 md:mx-0">
        <ProductTable
          isPremium={isPremium}
          appDownloadCTAHeadline={`Show top baits to catch ${speciesName} in the fishbrain app`}
          products={products}
          loadingRows={PRO_PAGE_SIZE}
          isLoading={loading}
          hasMoreRows={isPremium && !!data?.topProductUnits?.pageInfo.hasNextPage}
          onSeeMoreClicked={() => {
            setPageSize(pageSize + PRO_PAGE_SIZE);
          }}
        />
      </div>
    </div>
  );
};
